import type { FilterBodyType, TransformedCategoryFilterItemType } from '../types/categoryFilters';
import type { CategoryPageData } from '../types/categoryPageData';
import type { CategoryStateType } from '../types/categoryState';
import type { RootState } from '@app/store';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { OptionType } from '@shared/types/global';
import type { CardDisplayOptionType } from '@widgets/ProductCard';

import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';

import { ID_SUBCATEGORY_ATTRIBUTE, ID_TYPE_PRODUCT_ATTRIBUTE } from '@shared/constants/keys';
import { getProductCardData } from '@shared/utils/getProductCardData/getProductCardData';

import { getProductsForCategory } from '../../api/getProductsForCategory/getProductsForCategory';
import { addQueryParams } from '../../utils/addQueryParams/addQueryParams';

const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator }
});

export const initialState: CategoryStateType = {
  products: [],
  isError: false,
  view: 'grid',
  category: null,
  openAllFilters: false,
  filteredProductsCount: null,
  subCategory: undefined,
  sort: [
    { label: 'По возрастанию цены', value: 'asc', active: true },
    { label: 'По убыванию цены', value: 'desc', active: false }
  ],
  filters: {},
  activeFilters: {},
  prevActiveFilters: {},
  showMoreButtonLoading: false,
  resetButtonLoading: false,
  submitButtonLoading: false,
  pagination: {
    skip: 0,
    limit: 0,
    page: 0,
    total: 0,
    pageAll: 0
  }
};

export const categorySlice = createAppSlice({
  name: 'category',
  initialState,
  reducers: (create) => ({
    initState: create.reducer((state, action: PayloadAction<CategoryPageData>) => {
      const { allFilters, category, filters, pagination, products, count, ratings, subCategory, sort } = action.payload;

      state.pagination.total = +count;
      state.pagination.limit = +pagination.limit;
      state.pagination.skip = +pagination.skip;
      state.pagination.page =
        Math.ceil(+pagination.skip / +pagination.limit) > 0 ? Math.ceil(+pagination.skip / +pagination.limit) + 1 : 1;
      state.pagination.pageAll = Math.ceil(+count / +pagination.limit) > 1 ? Math.ceil(+count / +pagination.limit) : 1;

      state.products = getProductCardData(products, ratings);
      state.category = category;
      state.filteredProductsCount = count;
      state.subCategory = subCategory;

      state.sort = state.sort.map((el) => (el.value === sort ? { ...el, active: true } : { ...el, active: false }));
      state.filters = allFilters;
      state.activeFilters = filters;
      state.prevActiveFilters = filters;
    }),
    changeView: create.reducer((state, action: PayloadAction<CardDisplayOptionType>) => {
      state.view = action.payload;
    }),
    openAllFiltersModal: create.reducer((state) => {
      state.openAllFilters = true;
    }),
    closeAllFiltersModal: create.reducer((state) => {
      state.openAllFilters = false;
    }),
    changeRange: create.reducer((state, action: PayloadAction<{ min: string; max: string; id: string }>) => {
      const { min, max, id } = action.payload;

      if (min === '' && max === '') {
        delete state.activeFilters[id];
      } else {
        const baseMinValue = state.filters[id] && state.filters[id].min ? +state.filters[id].min : 0;
        const baseMaxValue = state.filters[id] && state.filters[id].max ? +state.filters[id].max : 0;

        const newMinValue = min ? +min : baseMinValue;
        const newMaxValue = max ? +max : baseMaxValue;

        state.activeFilters[id] = [newMinValue, newMaxValue];
      }
    }),
    changeList: create.reducer((state, action: PayloadAction<{ arr: OptionType[]; id: string }>) => {
      const { arr, id } = action.payload;
      if (state.activeFilters[id]) {
        const newValue = arr.filter((el) => el.active).map((el) => el.label);
        if (newValue.length) {
          state.activeFilters[id] = newValue;
        } else {
          delete state.activeFilters[id];
        }
      } else {
        state.activeFilters[id] = arr.filter((el) => el.active).map((el) => el.label);
      }
    }),
    changeSwitch: create.reducer((state, action: PayloadAction<{ value: boolean; id: string }>) => {
      const { value, id } = action.payload;
      if (value) {
        state.activeFilters[id] = [value];
      } else {
        if (state.activeFilters[id]) {
          delete state.activeFilters[id];
        } else {
          return;
        }
      }
    }),
    returnPrevFilterState: create.reducer((state) => {
      state.activeFilters = state.prevActiveFilters;
      state.filteredProductsCount = state.pagination.total.toString();
    }),
    changeSortValue: create.asyncThunk(
      async (value: string, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const category = store.category.category;
        const filters = store.category.activeFilters;
        const pagination = store.category.pagination;

        const body: FilterBodyType = {
          limit: pagination.limit,
          skip: 0,
          filters: filters,
          filtersHash: '',
          sort: value
        };

        const data = await getProductsForCategory({ category: category!.value, params: body });

        return {
          data
        };
      },
      {
        fulfilled: (state, action) => {
          const { pagination, filters, filtersHash, count, products, ratings, sort } = action.payload.data;

          state.activeFilters = filters;
          state.prevActiveFilters = filters;

          state.pagination.total = +count;
          state.pagination.limit = +pagination.limit;
          state.pagination.skip = +pagination.skip;
          state.pagination.page = 1;
          state.pagination.pageAll =
            Math.ceil(+count / +pagination.limit) > 1 ? Math.ceil(+count / +pagination.limit) : 1;

          state.products = getProductCardData(products, ratings);

          state.sort = state.sort.map((el) =>
            el.value === sort
              ? { ...el, active: true }
              : {
                  ...el,
                  active: false
                }
          );

          const queryString = `sort=${sort}&limit=${pagination.limit}&skip=${pagination.skip}${filtersHash ? `&filtersHash=${filtersHash}` : ''}`;
          addQueryParams(queryString);
        }
      }
    ),
    resetFilters: create.asyncThunk(
      async ({ signal }: { signal: AbortSignal }, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const pagination = store.category.pagination;
        const category = store.category.category;
        const subCategory = store.category.subCategory;

        const body: FilterBodyType = {
          limit: pagination.limit,
          skip: 0,
          filters: {},
          filtersHash: '',
          sort: 'asc'
        };

        if (category) {
          body.filters[ID_TYPE_PRODUCT_ATTRIBUTE] = [category.value];

          if (subCategory) {
            body.filters[ID_SUBCATEGORY_ATTRIBUTE] = [subCategory.value];
          }
        }

        const data = await getProductsForCategory({ category: category!.value, params: body, signal });

        return {
          data
        };
      },
      {
        pending: (state) => {
          state.resetButtonLoading = true;
        },
        fulfilled: (state, action) => {
          const { pagination, count, products, ratings, filters } = action.payload.data;

          state.pagination.total = +count;
          state.pagination.limit = +pagination.limit;
          state.pagination.skip = +pagination.skip;
          state.pagination.page = 1;
          state.pagination.pageAll =
            Math.ceil(+count / +pagination.limit) > 1 ? Math.ceil(+count / +pagination.limit) : 1;

          state.products = getProductCardData(products, ratings);
          state.filteredProductsCount = count;

          const newFilters: Record<string, TransformedCategoryFilterItemType> = {};

          for (const [key, item] of Object.entries(state.filters)) {
            if (item.type === 'String' && item.values) {
              newFilters[key] = {
                ...item,
                values: item.values.map((el) => ({ ...el, active: false }))
              };
            } else {
              newFilters[key] = item;
            }
          }

          state.filters = newFilters;
          state.activeFilters = filters;
          state.prevActiveFilters = filters;

          state.sort = state.sort.map((el) =>
            el.value === 'asc'
              ? { ...el, active: true }
              : {
                  ...el,
                  active: false
                }
          );

          const queryString = '';
          addQueryParams(queryString);
        },
        settled: (state) => {
          state.resetButtonLoading = false;
        }
      }
    ),
    submitFilters: create.asyncThunk(
      async (args: { update: boolean; signal?: AbortSignal }, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const pagination = store.category.pagination;
        const category = store.category.category;
        const filters = store.category.activeFilters;
        const sort = store.category.sort.find((el) => el.active);

        const body: FilterBodyType = {
          limit: pagination.limit,
          skip: 0,
          filters: filters,
          filtersHash: '',
          sort: sort ? sort.value : 'asc'
        };

        const data = await getProductsForCategory({
          category: category!.value,
          params: body,
          signal: args.signal
        });

        return {
          data
        };
      },
      {
        pending: (state, action) => {
          if (action.meta.arg.update) {
            state.submitButtonLoading = true;
          }
        },
        fulfilled: (state, action) => {
          const { pagination, filters, filtersHash, count, products, ratings, sort } = action.payload.data;

          if (action.meta.arg.update) {
            state.activeFilters = filters;
            state.prevActiveFilters = filters;

            state.products = getProductCardData(products, ratings);

            state.pagination.total = +count;
            state.pagination.limit = +pagination.limit;
            state.pagination.skip = +pagination.skip;
            state.pagination.page = 1;
            state.pagination.pageAll =
              Math.ceil(+count / +pagination.limit) > 1 ? Math.ceil(+count / +pagination.limit) : 1;

            state.openAllFilters = false;

            const queryString = `sort=${sort}&limit=${pagination.limit}&skip=${pagination.skip}${filtersHash ? `&filtersHash=${filtersHash}` : ''}`;
            addQueryParams(queryString);
          } else {
            state.filteredProductsCount = count.toString();
          }
        },
        settled: (state) => {
          state.submitButtonLoading = false;
        }
      }
    ),
    showMore: create.asyncThunk(
      async (_: undefined, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const pagination = store.category.pagination;
        const category = store.category.category;
        const filters = store.category.activeFilters;
        const sort = store.category.sort.find((el) => el.active);

        const body: FilterBodyType = {
          limit: pagination.limit,
          skip: +pagination.skip + +pagination.limit,
          filters: filters,
          filtersHash: '',
          sort: sort ? sort.value : 'asc'
        };

        const data = await getProductsForCategory({ category: category!.value, params: body });

        return {
          data
        };
      },
      {
        pending: (state) => {
          state.showMoreButtonLoading = true;
        },
        fulfilled: (state, action) => {
          const { pagination, filters, filtersHash, count, products, ratings, sort } = action.payload.data;

          state.activeFilters = filters;
          state.prevActiveFilters = filters;

          state.pagination.total = +count;
          state.pagination.limit = +pagination.limit;
          state.pagination.skip = +pagination.skip;
          state.pagination.page = state.pagination.page + 1;

          state.products = state.products.concat(getProductCardData(products, ratings));

          const queryString = `sort=${sort}&limit=${pagination.limit}&skip=${pagination.skip}${filtersHash ? `&filtersHash=${filtersHash}` : ''}`;
          addQueryParams(queryString);
        },
        settled: (state) => {
          state.showMoreButtonLoading = false;
        }
      }
    ),
    changePage: create.asyncThunk(
      async (value: number, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const pagination = store.category.pagination;
        const category = store.category.category;
        const filters = store.category.activeFilters;
        const sort = store.category.sort.find((el) => el.active);

        const body: FilterBodyType = {
          limit: pagination.limit,
          skip: value === 1 ? 0 : +pagination.limit * (value - 1),
          filters: filters,
          filtersHash: '',
          sort: sort ? sort.value : 'asc'
        };

        const data = await getProductsForCategory({ category: category!.value, params: body });

        return {
          data
        };
      },
      {
        fulfilled: (state, action) => {
          const { pagination, filters, filtersHash, count, products, ratings, sort } = action.payload.data;

          state.activeFilters = filters;
          state.prevActiveFilters = filters;

          state.pagination.total = +count;
          state.pagination.limit = +pagination.limit;
          state.pagination.skip = +pagination.skip;
          state.pagination.page = action.meta.arg;

          state.products = getProductCardData(products, ratings);

          const queryString = `sort=${sort}&limit=${pagination.limit}&skip=${pagination.skip}${filtersHash ? `&filtersHash=${filtersHash}` : ''}`;
          addQueryParams(queryString);
        }
      }
    )
  })
});

export const {
  changeView,
  openAllFiltersModal,
  closeAllFiltersModal,
  initState,
  showMore,
  changePage,
  resetFilters,
  changeList,
  changeSortValue,
  changeSwitch,
  changeRange,
  submitFilters,
  returnPrevFilterState
} = categorySlice.actions;

export const categoryReducer = categorySlice.reducer;
